import { useState } from "react";
import { cn } from "@/lib/utils";
import { useMyTasks, useReopenTask, useUndoCompleteTask, type MyTask } from "@/hooks/use-my-tasks";
import { groupByProject } from "./reviewed-tasks-grouping";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { formatHours } from "@/lib/format";
import { TaskComments } from "@/components/project/TaskComments";
import { useTaskCommentCounts } from "@/hooks/use-task-comments";
import { useHighlight } from "@/hooks/use-highlight";
import { usePersistedCollapse } from "@/hooks/use-persisted-collapse";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SrIcon } from "@/components/ui/SrIcon";

function StatusBadge({ status }: { status: string }) {
  const base = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[1.5px] text-[11px] font-medium";
  if (status === "completed") {
    return (
      <span className={cn(base, "bg-[#FEF3C7] text-[#78350F] border-foreground")}>
        <SrIcon name="clock" className="w-3 h-3" /> Venter godkjenning
      </span>
    );
  }
  if (status === "approved") {
    return (
      <span className={cn(base, "bg-[#D9F5E8] text-[#035C39] border-foreground")}>
        <SrIcon name="check-circle" className="w-3 h-3" /> Godkjent
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className={cn(base, "bg-[#FFE1DB] text-[#7F1D1D] border-foreground")}>
        <SrIcon name="x-circle" className="w-3 h-3" /> Avvist
      </span>
    );
  }
  return null;
}

function ReopenDialog({
  task,
  open,
  onOpenChange,
  onConfirm,
  isCompleted,
}: {
  task: MyTask;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: () => void;
  isCompleted: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{isCompleted ? "Angre innsending?" : "Gjenåpne oppgave?"}</AlertDialogTitle>
          <AlertDialogDescription>
            Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) flyttes tilbake til pågående{isCompleted ? "." : ", og du kan logge tid og sende til godkjenning på nytt."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{isCompleted ? "Angre" : "Gjenåpne"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ReviewedMobileCard({ task }: { task: MyTask }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const reopenMut = useReopenTask();
  const undoCompleteMut = useUndoCompleteTask();
  const isCompleted = task.task_status === "completed";

  const handleAction = () => {
    if (isCompleted) undoCompleteMut.mutate(task.id);
    else reopenMut.mutate(task.id);
    setShowConfirm(false);
  };

  return (
    <div className="border-b border-border/50 last:border-0 px-4 py-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{task.project_name}</p>
          <p className="text-xs text-muted-foreground break-words">
            {task.phase} — {task.task_description}
          </p>
        </div>
        <StatusBadge status={task.task_status} />
      </div>

      {task.task_status === "rejected" && task.rejection_reason && (
        <p className="text-xs text-red-600 bg-red-50 rounded-md px-2 py-1.5">
          {task.rejection_reason}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatHours(task.logged_hours)} / {formatHours(task.assigned_hours)}
        </span>
        <button
          onClick={() => setShowConfirm(true)}
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-card text-xs text-muted-foreground hover:text-foreground"
        >
          <SrIcon name="rotate-ccw" className="w-3.5 h-3.5"  />
          {isCompleted ? "Angre" : "Gjenåpne"}
        </button>
      </div>

      <ReopenDialog
        task={task}
        open={showConfirm}
        onOpenChange={(o) => !o && setShowConfirm(false)}
        onConfirm={handleAction}
        isCompleted={isCompleted}
      />
    </div>
  );
}

function ReviewedTaskRow({ task }: { task: MyTask }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const reopenMut = useReopenTask();
  const undoCompleteMut = useUndoCompleteTask();
  const { data: commentCounts } = useTaskCommentCounts([task.id]);
  const commentCount = commentCounts?.get(task.id) ?? 0;
  const highlightRef = useHighlight<HTMLTableRowElement>([task.id, task.budget_line_id]);

  const isApproved = task.task_status === "approved";
  const isRejected = task.task_status === "rejected";
  const isCompleted = task.task_status === "completed";

  const handleAction = () => {
    if (isCompleted) {
      undoCompleteMut.mutate(task.id);
    } else {
      reopenMut.mutate(task.id);
    }
    setShowConfirm(false);
  };

  return (
    <>
    <tr ref={highlightRef} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
      <td className="py-2.5 px-4 font-medium text-xs">{task.project_name}</td>
      <td className="py-2.5 px-4">
        <span className="text-xs text-muted-foreground">{task.phase}</span>
        <span className="text-xs text-muted-foreground mx-1">—</span>
        <span className="text-xs">{task.task_description}</span>
      </td>
      <td className="py-2.5 px-4 text-right tabular-nums text-sm">
        {formatHours(task.logged_hours)} / {task.assigned_hours.toFixed(1)}t
      </td>
      <td className="py-2.5 px-4">
        <div className="flex flex-col items-center gap-1">
          {isCompleted && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
              <SrIcon name="clock" className="w-3 h-3"  />
              Venter godkjenning
            </span>
          )}
          {isApproved && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
              <SrIcon name="check-circle2" className="w-3 h-3"  />
              Godkjent
            </span>
          )}
          {isRejected && (
            <div className="flex flex-col items-center gap-0.5">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-red-100 text-red-700">
                <SrIcon name="x-circle" className="w-3 h-3"  />
                Avvist
              </span>
              {task.rejection_reason && (
                <span className="text-[10px] text-red-600 max-w-[180px] text-center truncate" title={task.rejection_reason}>
                  {task.rejection_reason}
                </span>
              )}
            </div>
          )}
          <button
            className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
            onClick={() => setShowConfirm(true)}
          >
            <SrIcon name="rotate-ccw" className="w-3 h-3"  />
            {isCompleted ? "Angre" : "Gjenåpne"}
          </button>
          <button
            className={cn(
              "text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1",
              commentsOpen && "text-primary",
              commentCount > 0 && !commentsOpen && "text-primary font-medium",
            )}
            onClick={() => setCommentsOpen(!commentsOpen)}
            aria-expanded={commentsOpen}
          >
            <SrIcon name="message-square" className="w-3 h-3" />
            {commentCount > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[9px] font-bold rounded-full bg-primary text-primary-foreground tabular-nums">
                {commentCount}
              </span>
            ) : (
              "Kommentarer"
            )}
          </button>
        </div>
      </td>

      <AlertDialog open={showConfirm} onOpenChange={(open) => !open && setShowConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isCompleted ? "Angre innsending?" : "Gjenåpne oppgave?"}</AlertDialogTitle>
            <AlertDialogDescription>
              Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) flyttes tilbake til pågående{isCompleted ? "." : ", og du kan logge tid og sende til godkjenning på nytt."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}>{isCompleted ? "Angre" : "Gjenåpne"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </tr>
    {commentsOpen && (
      <tr className="bg-muted/30">
        <td colSpan={4} className="p-3">
          <TaskComments assignmentId={task.id} />
        </td>
      </tr>
    )}
    </>
  );
}

/** Collapsible per-project header + rader i «Behandlede oppgaver»-tabellen. */
function ReviewedProjectGroup({
  projectName,
  tasks,
}: {
  projectName: string;
  tasks: MyTask[];
}) {
  // VIB-2181 (Erik 03.09): prosjektgruppene er åpne som standard så
  // behandlede oppgaver er synlige uten et klikk per prosjekt.
  const [expanded, setExpanded] = useState(true);
  const waiting = tasks.filter((t) => t.task_status === "completed").length;
  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "border-b border-border/50 cursor-pointer transition-colors",
          expanded ? "bg-primary/5" : "hover:bg-muted/20",
        )}
      >
        <td colSpan={4} className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="font-medium truncate text-sm">{projectName}</p>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {tasks.length} oppgaver{waiting > 0 ? ` · ${waiting} venter` : ""}
              </span>
              <SrIcon name={expanded ? "chevron-up" : "chevron-down"} className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </td>
      </tr>
      {expanded && tasks.map((task) => <ReviewedTaskRow key={task.id} task={task} />)}
    </>
  );
}

export default function ReviewedTasksSection() {
  // VIB-2100: Helge vil ha denne lukket, og slippe å lukke den hver gang.
  const [expanded, setExpanded] = usePersistedCollapse("reviewed-tasks", true);
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data } = useMyTasks(userId);

  const reviewedTasks = (data?.tasks ?? []).filter(
    (t) => t.task_status === "completed" || t.task_status === "approved" || t.task_status === "rejected"
  );

  const waitingCount = reviewedTasks.filter((t) => t.task_status === "completed").length;
  const approvedCount = reviewedTasks.filter((t) => t.task_status === "approved").length;
  const rejectedCount = reviewedTasks.filter((t) => t.task_status === "rejected").length;

  const reviewedByProject = groupByProject(reviewedTasks);

  if (reviewedTasks.length === 0) return null;

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <SrIcon name="check-circle2" className="w-4 h-4 text-muted-foreground"  />
          <span className="font-display text-lg tracking-wide">Behandlede oppgaver</span>
          <div className="flex items-center gap-1.5">
            {waitingCount > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                {waitingCount} venter
              </span>
            )}
            {approvedCount > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                {approvedCount} godkjent
              </span>
            )}
            {rejectedCount > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                {rejectedCount} avvist
              </span>
            )}
          </div>
        </div>
        {expanded ? (
          <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground"  />
        ) : (
          <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground"  />
        )}
      </button>

      {expanded && (
        <div className="border-t">
          {/* Desktop: table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b">
                  <th className="text-left py-2 px-4 font-medium">Prosjekt</th>
                  <th className="text-left py-2 px-4 font-medium">Oppgave</th>
                  <th className="text-right py-2 px-4 font-medium">Timer</th>
                  <th className="text-center py-2 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {reviewedByProject.map(([pid, tasks]) => (
                  <ReviewedProjectGroup
                    key={pid}
                    projectName={tasks[0].project_name}
                    tasks={tasks}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list (prosjekt-gruppert rekkefølge, fase innen prosjekt) */}
          <div className="md:hidden">
            {reviewedByProject.flatMap(([, tasks]) => tasks).map((task) => (
              <ReviewedMobileCard key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
